import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  copyStorePaymentOptions,
  getStorePaymentOptions,
  saveStorePaymentOptions,
  getCountryPaymentOptions,
} from '../../../../utils/service-calls/payment-setup';
import { ButtonBlack, StoreSelect } from '../../../reusable';

const MODES = { CONFIGURE: 'CONFIGURE', COPY: 'COPY' };
const MODE_LABELS = { [MODES.CONFIGURE]: 'Configure Store', [MODES.COPY]: 'Copy Store' };

const emptyRow = () => ({
  mediaType: '',
  paymentOptionType: '',
  paymentType: '',
  rowId: uuidv4(),
});

const isDuplicateRow = (rows, candidate) => rows.some(
  (r) => r.mediaType === candidate.mediaType
         && r.paymentOptionType === candidate.paymentOptionType
         && r.paymentType === candidate.paymentType,
);

const parsePaymentOptionsJson = (jsonString) => {
  const parsed = JSON.parse(jsonString);
  const options = parsed?.paymentOptions || parsed;
  if (!Array.isArray(options)) {
    throw new Error('Expected a "paymentOptions" array');
  }
  return options
    .filter((o) => o && typeof o === 'object' && o.mediaType)
    .map((o) => ({
      mediaType: o.mediaType || '',
      paymentOptionType: o.paymentOptionType || '',
      paymentType: o.paymentType || '',
    }));
};

const PaymentConfigTab = ({ selectedCountry, hasNextTab, onSaveAndNext }) => {
  const userToken = useSelector((state) => state.authorizationReducer.auth.accessToken);
  const fileInputRef = useRef(null);

  const [mode, setMode] = useState(MODES.COPY);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  // --- Country reference options (shared) ---
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryOptionsLoaded, setCountryOptionsLoaded] = useState(false);
  const [countryLoading, setCountryLoading] = useState(false);

  // --- Copy Store state ---
  const [selectedSourceStore, setSelectedSourceStore] = useState(null);
  const [selectedDestStore, setSelectedDestStore] = useState(null);
  const [override, setOverride] = useState(false);
  const [excludeTypes, setExcludeTypes] = useState([]);
  const [sourceOptions, setSourceOptions] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);

  // --- Configure Store state ---
  const [selectedConfigStore, setSelectedConfigStore] = useState(null);
  const [configFetched, setConfigFetched] = useState(false);
  const [configRows, setConfigRows] = useState(() => [emptyRow()]);
  const [configLoading, setConfigLoading] = useState(false);
  const [emptyStoreMessage, setEmptyStoreMessage] = useState('');

  // --- Collapsible sections ---
  const [countryTableExpanded, setCountryTableExpanded] = useState(true);
  const [configTableExpanded, setConfigTableExpanded] = useState(true);

  // --- JSON paste ---
  const [jsonPasteOpen, setJsonPasteOpen] = useState(false);
  const [jsonPasteValue, setJsonPasteValue] = useState('');

  // Fetch country options on mount / country change
  const fetchCountryOptions = useCallback(async () => {
    if (!selectedCountry?.value) return;
    setCountryLoading(true);
    try {
      const res = await getCountryPaymentOptions(userToken, selectedCountry.value);
      setCountryOptions(res.body?.paymentOptions || []);
      setCountryOptionsLoaded(true);
    } catch {
      setCountryOptions([]);
      setCountryOptionsLoaded(true);
    } finally {
      setCountryLoading(false);
    }
  }, [userToken, selectedCountry]);

  useEffect(() => {
    setCountryOptionsLoaded(false);
    setCountryOptions([]);
    if (selectedCountry?.value) {
      fetchCountryOptions();
    }
  }, [selectedCountry?.value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive unique media types from country options
  const countryMediaTypes = useMemo(
    () => [...new Set(countryOptions.map((o) => o.mediaType).filter(Boolean))],
    [countryOptions],
  );

  // Given a mediaType, return the matching paymentOptionTypes
  // Given a mediaType + paymentOptionType, return the matching paymentTypes
  const getOptionsForMedia = useCallback((mediaType) => {
    const filtered = countryOptions.filter((o) => o.mediaType === mediaType);
    return [...new Set(filtered.map((o) => o.paymentOptionType).filter(Boolean))];
  }, [countryOptions]);

  const getPaymentTypesFor = useCallback((mediaType, paymentOptionType) => {
    const filtered = countryOptions.filter(
      (o) => o.mediaType === mediaType && o.paymentOptionType === paymentOptionType,
    );
    return [...new Set(filtered.map((o) => o.paymentType).filter(Boolean))];
  }, [countryOptions]);

  // ---- Copy Store handlers ----

  const handleFetchSourceOptions = useCallback(async () => {
    if (!selectedSourceStore) return;
    setSourceLoading(true);
    setStatus(null);
    try {
      const res = await getStorePaymentOptions(userToken, selectedSourceStore.id);
      setSourceOptions(res.body?.paymentOptions || []);
    } catch (err) {
      setStatus({ isError: true, message: `Failed to fetch source store: ${err?.message || 'Unknown error'}` });
      setSourceOptions(null);
    } finally {
      setSourceLoading(false);
    }
  }, [userToken, selectedSourceStore]);

  const handleOverrideToggle = useCallback(() => {
    if (!override) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        'Enabling Override will permanently delete ALL existing payment options at the destination store before copying. Are you sure?',
      );
      if (!confirmed) return;
    }
    setOverride((prev) => !prev);
  }, [override]);

  const handleCopy = useCallback(async () => {
    if (!selectedSourceStore || !selectedDestStore) return;
    setLoading(true);
    setStatus(null);
    try {
      const body = {
        destStoreId: selectedDestStore.id,
        excludePaymentTypes: excludeTypes.length > 0 ? excludeTypes : undefined,
        override,
        sourceStoreId: selectedSourceStore.id,
      };
      const res = await copyStorePaymentOptions(userToken, body);
      const { copiedCount = 0, deletedCount = 0 } = res.body || {};
      setStatus({
        isError: false,
        message: `Copy succeeded — ${copiedCount} option(s) copied${override ? `, ${deletedCount} deleted` : ''}.`,
      });
    } catch (err) {
      setStatus({ isError: true, message: `Copy failed: ${err?.message || 'Unknown error'}` });
    } finally {
      setLoading(false);
    }
  }, [userToken, selectedSourceStore, selectedDestStore, override, excludeTypes]);

  // ---- Configure Store handlers ----

  const handleFetchConfigStore = useCallback(async () => {
    if (!selectedConfigStore) return;
    setConfigLoading(true);
    setStatus(null);
    setEmptyStoreMessage('');
    try {
      const res = await getStorePaymentOptions(userToken, selectedConfigStore.id);
      const options = res.body?.paymentOptions || [];
      setConfigFetched(true);
      if (options.length > 0) {
        setConfigRows(options.map((o) => ({
          isExisting: true,
          mediaType: o.mediaType || '',
          paymentOptionType: o.paymentOptionType || '',
          paymentType: o.paymentType || '',
          rowId: uuidv4(),
        })));
      } else {
        setConfigRows([emptyRow()]);
        setEmptyStoreMessage('No payment options configured for this store yet.');
        setTimeout(() => setEmptyStoreMessage(''), 5000);
      }
    } catch (err) {
      setStatus({ isError: true, message: `Failed to fetch store: ${err?.message || 'Unknown error'}` });
      setConfigFetched(false);
    } finally {
      setConfigLoading(false);
    }
  }, [userToken, selectedConfigStore]);

  const handleAddRow = useCallback(() => {
    setConfigRows((prev) => [...prev, emptyRow()]);
  }, []);

  const handleRemoveRow = useCallback((index) => {
    setConfigRows((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [emptyRow()];
    });
  }, []);

  const handleRowChange = useCallback((index, field, value) => {
    setConfigRows((prev) => prev.map((row, i) => {
      if (i !== index) return row;
      if (field === 'mediaType') {
        return {
          ...row, mediaType: value, paymentOptionType: '', paymentType: '',
        };
      }
      if (field === 'paymentOptionType') {
        return { ...row, paymentOptionType: value, paymentType: '' };
      }
      return { ...row, [field]: value };
    }));
  }, []);

  const handleAddFromCountry = useCallback((opt) => {
    const candidate = {
      mediaType: opt.mediaType || '',
      paymentOptionType: opt.paymentOptionType || '',
      paymentType: opt.paymentType || '',
    };
    setConfigRows((prev) => {
      if (isDuplicateRow(prev, candidate)) return prev;
      const cleaned = prev.filter((r) => r.mediaType || r.paymentOptionType || r.paymentType);
      return [...cleaned, { ...candidate, rowId: uuidv4() }];
    });
  }, []);

  const importRows = useCallback((parsed) => {
    setConfigRows((prev) => {
      const cleaned = prev.filter((r) => r.mediaType || r.paymentOptionType || r.paymentType);
      return [
        ...cleaned,
        ...parsed.map((p) => ({ ...p, rowId: uuidv4() })),
      ];
    });
    return parsed.length;
  }, []);

  const handleFileImport = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parsePaymentOptionsJson(ev.target.result);
        if (parsed.length === 0) {
          setStatus({ isError: true, message: 'No valid payment options found in file.' });
          return;
        }
        const added = importRows(parsed);
        setStatus({ isError: false, message: `Imported ${added} option(s) from file.` });
      } catch (err) {
        setStatus({ isError: true, message: `Invalid JSON file: ${err.message}` });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, [importRows]);

  const handlePasteImport = useCallback(() => {
    if (!jsonPasteValue.trim()) return;
    try {
      const parsed = parsePaymentOptionsJson(jsonPasteValue);
      if (parsed.length === 0) {
        setStatus({ isError: true, message: 'No valid payment options found in pasted JSON.' });
        return;
      }
      const added = importRows(parsed);
      setStatus({ isError: false, message: `Imported ${added} option(s).` });
      setJsonPasteValue('');
      setJsonPasteOpen(false);
    } catch (err) {
      setStatus({ isError: true, message: `Invalid JSON: ${err.message}` });
    }
  }, [jsonPasteValue, importRows]);

  const handleSaveConfig = useCallback(async () => {
    const newRows = configRows
      .filter((r) => !r.isExisting && r.mediaType.trim() && r.paymentOptionType.trim());
    const validRows = newRows
      .map(({ mediaType, paymentOptionType, paymentType }) => ({
        mediaType, paymentOptionType, paymentType: paymentType || '',
      }));
    if (!selectedConfigStore || validRows.length === 0) return;
    setLoading(true);
    setStatus(null);
    try {
      const body = { paymentOptions: validRows };
      const res = await saveStorePaymentOptions(userToken, selectedConfigStore.id, body);
      const { savedCount = 0, skippedCount = 0 } = res.body || {};
      setStatus({
        isError: false,
        message: `Save succeeded — ${savedCount} option(s) saved, ${skippedCount} skipped (duplicates).`,
      });
      // Mark newly saved rows as existing so they become read-only
      setConfigRows((prev) => prev.map((r) => (r.isExisting ? r : { ...r, isExisting: true })));
      if (hasNextTab && onSaveAndNext) {
        onSaveAndNext();
      }
    } catch (err) {
      setStatus({ isError: true, message: `Save failed: ${err?.message || 'Unknown error'}` });
    } finally {
      setLoading(false);
    }
  }, [userToken, selectedConfigStore, configRows, hasNextTab, onSaveAndNext]);

  // ---- Render helpers ----

  const renderSegmentedControl = () => {
    const modes = [MODES.COPY, MODES.CONFIGURE];
    const activeIndex = modes.indexOf(mode);
    return (
      <div className="soa-workflow-section">
        <div className="soa-segmented">
          <div
            className="soa-segmented-indicator"
            style={{
              transform: `translateX(${activeIndex * 100}%)`,
              width: `calc((100% - 8px) / ${modes.length})`,
            }}
          />
          {modes.map((m) => (
            <button
              key={m}
              className={`soa-segmented-btn ${mode === m ? 'soa-segmented-btn-active' : ''}`}
              type="button"
              onClick={() => { setMode(m); setStatus(null); }}
            >
              <span className="soa-segmented-label">{MODE_LABELS[m]}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderPaymentOptionsTable = (options, caption) => {
    if (!options || options.length === 0) {
      return <p className="soa-payment-empty">No payment options found{caption ? ` ${caption}` : ''}.</p>;
    }
    return (
      <table className="soa-table soa-payment-table">
        <thead>
          <tr>
            <th>Media Type</th>
            <th>Payment Option Type</th>
            <th>Payment Type</th>
          </tr>
        </thead>
        <tbody>
          {options.map((opt, i) => (
            <tr key={`${opt.mediaType}-${opt.paymentOptionType}-${opt.paymentType}`} className={i % 2 === 0 ? '' : 'soa-row-even'}>
              <td>{opt.mediaType}</td>
              <td>
                <span className={`pill ${opt.paymentOptionType === 'POSITIVE' ? 'pill-positive' : 'pill-negative'}`}>
                  {opt.paymentOptionType}
                </span>
              </td>
              <td>{opt.paymentType}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderCollapsibleHeader = (title, count, expanded, onToggle) => (
    <button
      className="soa-payment-collapsible-header"
      type="button"
      onClick={onToggle}
    >
      <span className={`soa-payment-collapsible-chevron ${expanded ? 'soa-payment-collapsible-chevron-open' : ''}`}>
        &#9654;
      </span>
      <span className="soa-payment-collapsible-title">{title}</span>
      {count !== undefined && (
        <span className="soa-payment-collapsible-count">({count})</span>
      )}
    </button>
  );

  const renderCountryOptionsInline = () => {
    if (!countryOptionsLoaded && countryLoading) {
      return <p className="soa-payment-hint">Loading country payment options...</p>;
    }
    if (!countryOptionsLoaded || countryOptions.length === 0) return null;

    return (
      <div className="soa-payment-collapsible-section">
        {renderCollapsibleHeader(
          `${selectedCountry?.value || 'Country'} Payment Options`,
          countryOptions.length,
          countryTableExpanded,
          () => setCountryTableExpanded((prev) => !prev),
        )}
        {countryTableExpanded && (
          <table className="soa-table soa-payment-table soa-payment-country-inline-table">
            <thead>
              <tr>
                <th>Media Type</th>
                <th>Payment Option Type</th>
                <th>Payment Type</th>
                <th aria-label="Add" style={{ width: '50px' }} />
              </tr>
            </thead>
            <tbody>
              {countryOptions.map((opt, i) => {
                const alreadyAdded = isDuplicateRow(configRows, opt);
                return (
                  <tr key={`${opt.mediaType}-${opt.paymentOptionType}-${opt.paymentType}`} className={i % 2 === 0 ? '' : 'soa-row-even'}>
                    <td>{opt.mediaType}</td>
                    <td>
                      <span className={`pill ${opt.paymentOptionType === 'POSITIVE' ? 'pill-positive' : 'pill-negative'}`}>
                        {opt.paymentOptionType}
                      </span>
                    </td>
                    <td>{opt.paymentType}</td>
                    <td>
                      <button
                        className={`soa-payment-add-from-country ${alreadyAdded ? 'soa-payment-add-from-country-added' : ''}`}
                        disabled={alreadyAdded}
                        title={alreadyAdded ? 'Already added' : 'Add to configuration'}
                        type="button"
                        onClick={() => handleAddFromCountry(opt)}
                      >
                        {alreadyAdded ? '✓' : '+'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderCopyStore = () => {
    const bothSelected = selectedSourceStore && selectedDestStore;
    return (
      <div className="soa-payment-form">
        <div className="soa-payment-field">
          <span className="soa-payment-label">
            Source Store <span className="soa-payment-req">*</span>
          </span>
          <div className="soa-payment-input-row soa-payment-store-select-row">
            <div className="soa-payment-store-select">
              <StoreSelect
                fullWidth
                countryRestriction={selectedCountry || {}}
                selectCountry={() => {}}
                selectStore={setSelectedSourceStore}
                selectedCountry={selectedCountry}
                selectedStore={selectedSourceStore}
              />
            </div>
            <button
              className="soa-payment-fetch-btn"
              disabled={!selectedSourceStore || sourceLoading}
              type="button"
              onClick={handleFetchSourceOptions}
            >
              {sourceLoading ? 'Fetching...' : 'Fetch Options'}
            </button>
          </div>
          <span className="soa-payment-hint">Store to copy payment options FROM.</span>
        </div>

        {sourceOptions !== null && (
          <div className="soa-payment-preview">
            <div className="soa-payment-preview-title">
              Source Store Payment Options ({sourceOptions.length})
            </div>
            {renderPaymentOptionsTable(sourceOptions, 'for this store')}
          </div>
        )}

        <div className="soa-payment-field">
          <span className="soa-payment-label">
            Destination Store <span className="soa-payment-req">*</span>
          </span>
          <div className="soa-payment-input-row">
            <div className="soa-payment-store-select">
              <StoreSelect
                fullWidth
                countryRestriction={selectedCountry || {}}
                selectCountry={() => {}}
                selectStore={setSelectedDestStore}
                selectedCountry={selectedCountry}
                selectedStore={selectedDestStore}
              />
            </div>
          </div>
          <span className="soa-payment-hint">Store to copy payment options TO.</span>
        </div>

        {bothSelected && (
          <>
            <div className="soa-payment-field">
              <span className="soa-payment-label">Override Existing Options</span>
              <div className="soa-payment-toggle-row">
                <button
                  aria-checked={override}
                  aria-label="Override existing options"
                  className={`soa-payment-switch ${override ? 'soa-payment-switch-on' : ''}`}
                  role="switch"
                  type="button"
                  onClick={handleOverrideToggle}
                >
                  <span className="soa-payment-switch-knob" />
                </button>
                <span className="soa-payment-toggle-label">
                  Override: {override ? 'ON' : 'OFF'}
                </span>
              </div>
              {override && (
                <div className="soa-payment-override-warning">
                  All existing payment options at the destination store will be deleted before copying.
                </div>
              )}
            </div>

            <div className="soa-payment-field">
              <span className="soa-payment-label">Exclude Payment Types</span>
              {sourceOptions !== null && sourceOptions.length > 0 ? (
                <div className="soa-payment-checkbox-list">
                  {[...new Set(sourceOptions.map((opt) => opt.paymentType))].map((type) => (
                    <label key={type} className="soa-payment-checkbox-item">
                      <input
                        checked={excludeTypes.includes(type)}
                        type="checkbox"
                        onChange={() => {
                          setExcludeTypes((prev) => (prev.includes(type)
                            ? prev.filter((t) => t !== type)
                            : [...prev, type]));
                        }}
                      />
                      <span className="soa-payment-checkbox-label">{type}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <span className="soa-payment-hint">Fetch source store options first to see available payment types.</span>
              )}
              <span className="soa-payment-hint">Payment types to skip during the copy.</span>
            </div>

            <div className="soa-payment-actions">
              <ButtonBlack
                isDisabled={!selectedSourceStore || !selectedDestStore || loading}
                isLoading={loading}
                label="Copy Payment Options"
                onClick={handleCopy}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const renderConfigureStore = () => {
    const newValidRows = configRows.filter(
      (r) => !r.isExisting && r.mediaType.trim() && r.paymentOptionType.trim(),
    );
    const canSave = selectedConfigStore && newValidRows.length > 0 && !loading;
    return (
      <div className="soa-payment-form">
        <div className="soa-payment-field">
          <span className="soa-payment-label">
            Store <span className="soa-payment-req">*</span>
          </span>
          <div className="soa-payment-input-row soa-payment-store-select-row">
            <div className="soa-payment-store-select">
              <StoreSelect
                fullWidth
                countryRestriction={selectedCountry || {}}
                selectCountry={() => {}}
                selectStore={setSelectedConfigStore}
                selectedCountry={selectedCountry}
                selectedStore={selectedConfigStore}
              />
            </div>
            <button
              className="soa-payment-fetch-btn"
              disabled={!selectedConfigStore || configLoading}
              type="button"
              onClick={handleFetchConfigStore}
            >
              {configLoading ? 'Fetching...' : 'Fetch Options'}
            </button>
          </div>
          <span className="soa-payment-hint">The store being configured.</span>
        </div>

        {emptyStoreMessage && (
          <div className="soa-payment-empty-state">{emptyStoreMessage}</div>
        )}

        {configFetched && (
          <>
            {/* Inline country options with + buttons */}
            {renderCountryOptionsInline()}

            {/* Store config table */}
            <div className="soa-payment-collapsible-section">
              {renderCollapsibleHeader(
                'Store Payment Options',
                configRows.filter((r) => r.mediaType || r.paymentOptionType || r.paymentType).length,
                configTableExpanded,
                () => setConfigTableExpanded((prev) => !prev),
              )}
              {configTableExpanded && (
              <>
                <table className="soa-table soa-payment-table soa-payment-edit-table">
                  <thead>
                    <tr>
                      <th>Media Type</th>
                      <th>Payment Option Type</th>
                      <th>Payment Type</th>
                      <th aria-label="Actions" style={{ width: '80px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {configRows.map((row, i) => {
                    if (row.isExisting) {
                      return (
                        <tr key={row.rowId} className={i % 2 === 0 ? '' : 'soa-row-even'}>
                          <td>{row.mediaType}</td>
                          <td>{row.paymentOptionType}</td>
                          <td>{row.paymentType}</td>
                          <td aria-label="No actions" />
                        </tr>
                      );
                    }

                    const mediaSelected = !!row.mediaType;
                    const potSelected = !!row.paymentOptionType;

                    const countryPotOpts = mediaSelected ? getOptionsForMedia(row.mediaType) : [];
                    const countryPtOpts = mediaSelected && potSelected
                      ? getPaymentTypesFor(row.mediaType, row.paymentOptionType)
                      : [];

                    const mediaOpts = countryMediaTypes.includes(row.mediaType)
                      ? countryMediaTypes
                      : [...countryMediaTypes, ...(row.mediaType ? [row.mediaType] : [])];
                    const potOpts = countryPotOpts.includes(row.paymentOptionType)
                      ? countryPotOpts
                      : [...countryPotOpts, ...(row.paymentOptionType ? [row.paymentOptionType] : [])];
                    const ptOpts = countryPtOpts.includes(row.paymentType)
                      ? countryPtOpts
                      : [...countryPtOpts, ...(row.paymentType ? [row.paymentType] : [])];

                    return (
                      <tr key={row.rowId} className={i % 2 === 0 ? '' : 'soa-row-even'}>
                        {/* Media Type */}
                        <td>
                          <select
                            aria-label={`Media type for row ${i + 1}`}
                            className="soa-payment-select"
                            value={row.mediaType}
                            onChange={(e) => handleRowChange(i, 'mediaType', e.target.value)}
                          >
                            <option value="">— Select —</option>
                            {mediaOpts.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </td>

                        {/* Payment Option Type — disabled until media type selected */}
                        <td>
                          <select
                            aria-label={`Payment option type for row ${i + 1}`}
                            className={`soa-payment-select ${!mediaSelected ? 'soa-payment-select-disabled' : ''}`}
                            disabled={!mediaSelected}
                            value={row.paymentOptionType}
                            onChange={(e) => handleRowChange(i, 'paymentOptionType', e.target.value)}
                          >
                            <option value="">— Select —</option>
                            {potOpts.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </td>

                        {/* Payment Type — disabled until payment option type selected */}
                        <td>
                          <select
                            aria-label={`Payment type for row ${i + 1}`}
                            className={`soa-payment-select ${!potSelected ? 'soa-payment-select-disabled' : ''}`}
                            disabled={!potSelected}
                            value={row.paymentType}
                            onChange={(e) => handleRowChange(i, 'paymentType', e.target.value)}
                          >
                            <option value="">— Select —</option>
                            {ptOpts.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <button
                            className="soa-payment-remove-btn"
                            type="button"
                            onClick={() => handleRemoveRow(i)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>

                <div className="soa-payment-table-actions">
                  <button className="soa-payment-add-row" type="button" onClick={handleAddRow}>
                    + Add another payment option
                  </button>

                  <button
                    className="soa-payment-import-btn"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Import JSON File
                  </button>
                  <input
                    ref={fileInputRef}
                    accept=".json"
                    className="soa-payment-file-input"
                    type="file"
                    onChange={handleFileImport}
                  />

                  <button
                    className={`soa-payment-import-btn ${jsonPasteOpen ? 'soa-payment-import-btn-active' : ''}`}
                    type="button"
                    onClick={() => setJsonPasteOpen((prev) => !prev)}
                  >
                    Paste JSON
                  </button>
                </div>

                {jsonPasteOpen && (
                <div className="soa-payment-paste-section">
                  <textarea
                    className="soa-payment-paste-textarea"
                    placeholder='Paste JSON here, e.g. { "paymentOptions": [ ... ] }'
                    rows={6}
                    value={jsonPasteValue}
                    onChange={(e) => setJsonPasteValue(e.target.value)}
                  />
                  <button
                    className="soa-payment-fetch-btn"
                    disabled={!jsonPasteValue.trim()}
                    type="button"
                    onClick={handlePasteImport}
                  >
                    Apply
                  </button>
                </div>
              )}
              </>
              )}
            </div>

            <div className="soa-payment-actions">
              <ButtonBlack
                isDisabled={!canSave}
                isLoading={loading}
                label={hasNextTab ? 'Save and Continue' : 'Save Payment Options'}
                onClick={handleSaveConfig}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <section className="soa-payment-tab">
      {renderSegmentedControl()}

      {status && (
        <p className={`soa-status-message ${status.isError ? 'soa-status-warning' : 'soa-status-success'}`}>
          {status.message}
        </p>
      )}

      {mode === MODES.COPY && renderCopyStore()}
      {mode === MODES.CONFIGURE && renderConfigureStore()}
    </section>
  );
};

PaymentConfigTab.defaultProps = {
  hasNextTab: false,
  onSaveAndNext: null,
  selectedCountry: null,
};

PaymentConfigTab.propTypes = {
  hasNextTab: PropTypes.bool,
  onSaveAndNext: PropTypes.func,
  selectedCountry: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
};

export default PaymentConfigTab;
